import { QueryClient } from "@tanstack/query-core";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { SetErrorMessageType } from "@/app/providers/ReactContextProvider/types";
import { BiometryStatus } from "@/entities/biometry/types";
import { getLeadQueryOption } from "@/entities/lead/api/getLeadQueryOption";
import { LeadStatus } from "@/entities/lead/model/types";
import { getLoanInfoQueryOption } from "@/entities/loan/api/getLoanInfoQueryOption";
import { LoanStatus } from "@/entities/loan/model/types";
import { getProfileQueryOption } from "@/entities/profile/api/getProfileQueryOption";
import { RouterPath } from "@/router/types";

export const cabinetLoader =
  (queryClient: QueryClient, setErrorMessage: SetErrorMessageType) =>
  async <Context = unknown>({ request }: LoaderFunctionArgs<Context>) => {
    const token = localStorage.getItem("token");
    if (!token) {
      return redirect(RouterPath.Login);
    }

    const splittedUrl = request?.url?.split("/");
    const requestPath = `/${splittedUrl?.[splittedUrl?.length - 1]}`;

    const profilePromise = queryClient.fetchQuery(
      getProfileQueryOption({
        token,
        setErrorMessage,
      }),
    );

    const latestLeadPromise = queryClient.fetchQuery(
      getLeadQueryOption({
        token,
        setErrorMessage,
      }),
    );

    const [profile, latestLead] = await Promise.all([
      profilePromise,
      latestLeadPromise,
    ]);

    const isLeadBiometry =
      !profile?.biometry ||
      profile.biometry.status !== BiometryStatus.Success ||
      !profile.informations_requested;

    if (isLeadBiometry) {
      if (requestPath.includes(RouterPath.LeadBiometry)) {
        return {
          profile,
          latestLead,
        };
      } else {
        return redirect(RouterPath.LeadBiometry);
      }
    }

    const isProfileRefill =
      !!profile &&
      !profile.registration_completed &&
      profile.informations_requested;

    if (isProfileRefill) {
      if (requestPath.includes(RouterPath.ProfileRefill)) {
        return {
          profile,
          latestLead,
        };
      } else {
        return redirect(RouterPath.ProfileRefill);
      }
    }

    const isLeadApply =
      !!profile?.registration_completed &&
      (latestLead === null ||
        latestLead?.status === LeadStatus.LookingEmploymentContracts ||
        latestLead?.status === LeadStatus.TokenRequest ||
        latestLead?.status === LeadStatus.ChooseEmploymentContract ||
        latestLead?.status === LeadStatus.ChooseLoanParams);

    if (isLeadApply) {
      if (requestPath.includes(RouterPath.Cabinet)) {
        return redirect(RouterPath.LeadApply);
      }

      return {
        profile,
        latestLead,
      };
    }

    const isBlocking = !!profile?.blocking?.expires_at;

    const isLoanRepeat =
      !isBlocking &&
      (latestLead?.status === LeadStatus.Closed ||
        latestLead?.status === LeadStatus.CustomerRefusal);

    if (isLoanRepeat) {
      if (requestPath.includes(RouterPath.Cabinet)) {
        return redirect(RouterPath.LoanRepeat);
      }

      return {
        profile,
        latestLead,
      };
    }

    const isLeadSign =
      !!profile?.registration_completed &&
      latestLead?.status === LeadStatus.PendingSMS;

    if (isLeadSign) {
      if (requestPath.includes(RouterPath.LeadSign)) {
        return {
          profile,
          latestLead,
        };
      } else {
        return redirect(RouterPath.LeadSign);
      }
    }

    const loanInfoPromise = queryClient.fetchQuery(
      getLoanInfoQueryOption({
        token,
        setErrorMessage,
        leadId: latestLead?.lead_id,
      }),
    );

    const loanInfo = await loanInfoPromise;

    const isRefinance = !!loanInfo?.parent_loan_id;

    const isLoanSubmit =
      !!profile?.registration_completed &&
      (latestLead?.status === LeadStatus.PendingSubmit ||
        loanInfo?.status === LoanStatus.PendingEmail ||
        loanInfo?.status === LoanStatus.PendingSubmit) &&
      !isRefinance;

    if (isLoanSubmit) {
      if (requestPath.includes(RouterPath.LoanSubmit)) {
        return {
          profile,
          latestLead,
          loanInfo,
        };
      } else {
        return redirect(RouterPath.LoanSubmit);
      }
    }

    const isLoanBiometry = loanInfo?.status === LoanStatus.PendingBiometry;

    if (isLoanBiometry) {
      if (requestPath.includes(RouterPath.LoanBiometry)) {
        return {
          profile,
          latestLead,
          loanInfo,
        };
      } else {
        return redirect(RouterPath.LoanBiometry);
      }
    }

    const isCabinet =
      !isLeadBiometry &&
      !isProfileRefill &&
      !isLeadApply &&
      !isLeadSign &&
      !isLoanSubmit &&
      !isLoanBiometry;

    if (
      isCabinet &&
      requestPath !== RouterPath.Cabinet &&
      requestPath !== RouterPath.History &&
      requestPath !== RouterPath.Profile
    ) {
      return redirect(RouterPath.Cabinet);
    }

    return {
      profile,
      latestLead,
      loanInfo,
    };
  };
